<script lang="ts">
  export * from './FileUpload.constants';
</script>

<script setup lang="ts">
  import logger from '@leaflink/snitch';
  import { computed, inject, ref, useAttrs, useCssModule } from 'vue';
  import InlineSvg from 'vue-inline-svg';

  import { StashProvideState } from '../../../types/misc';
  import { t } from '../../locale';
  import Button from '../Button/Button.vue';
  import Icon from '../Icon/Icon.vue';
  import { FILE_TYPES, FileUploadSize, FileUploadSizes } from './FileUpload.constants';

  export type FileType = 'CSV' | 'PDF' | 'PNG' | 'JPEG' | 'DOC' | 'XLS';

  export interface FileUploadProps {
    /**
     * Files to display in the component
     */
    files?: File[];

    /**
     * Accepted file types
     */
    fileTypes?: FileType[];

    /**
     * Should display only the button
     */
    buttonOnly?: boolean;

    /**
     * Allows upload of multiple files
     */
    multiple?: boolean;

    /**
     * Is the input disabled
     */
    disabled?: boolean;

    /**
     * Component size
     */
    size?: FileUploadSize;
  }

  const props = withDefaults(defineProps<FileUploadProps>(), {
    files: () => [],
    fileTypes: () => ['CSV', 'PDF', 'PNG', 'JPEG', 'DOC', 'XLS'],
    buttonOnly: false,
    disabled: false,
    multiple: false,
    size: 'standard',
  });

  const classes = useCssModule();

  const emit = defineEmits<{
    (e: 'file-select', { files }: { files: FileUploadProps['files'] }): void;
    (e: 'file-delete', file: File): void;
    (e: 'file-error', message: string): void;
  }>();

  const isDraggingOver = ref(false);
  const fileUploadRef = ref<HTMLInputElement>();

  const stashOptions = inject<StashProvideState>('stashOptions');
  const attributes = useAttrs();

  const inputAttrs = computed(() => {
    const attrs = { ...attributes };

    delete attrs['data-test'];
    delete attrs.class;
    delete attrs.type;
    delete attrs.accept;

    return attrs;
  });

  function concatArraysToFirst(a: string[], b: string[]) {
    return a.concat(b);
  }

  const acceptedMimeTypes = computed(() => {
    return props.fileTypes.map((fileType) => FILE_TYPES[fileType].MIME_TYPES).reduce(concatArraysToFirst);
  });

  const acceptedFileExtensions = computed(() => {
    return props.fileTypes.map((fileType) => FILE_TYPES[fileType].EXTENSION).reduce(concatArraysToFirst);
  });

  const illustrationPath = computed(() => {
    return `${stashOptions?.staticPath}/illustrations/FileUpload/${FILE_TYPES[props.fileTypes[0]].ILLUSTRATION}.svg`;
  });

  function openFileDialog() {
    if (fileUploadRef.value) {
      fileUploadRef.value.value = '';
      fileUploadRef.value.click();
    }
  }

  function handleDragEnter() {
    isDraggingOver.value = true;
  }

  function handleDragLeave() {
    isDraggingOver.value = false;
  }

  function handleFileError(error: Error) {
    const message = t('ll.fileUpload.errors.incorrectFileType', {
      fileTypes: acceptedFileExtensions.value.join(', '),
    });

    emit('file-error', message);

    logger.log(error);
  }

  async function areFileTypesAccepted(files: File[]) {
    if (!acceptedMimeTypes.value.length) return true;

    const mimeTypes = await Promise.all(files.map((file) => readMimeType(file)));

    const allCorrectMimeTypes =
      !!mimeTypes.length && mimeTypes.every((mimeType) => acceptedMimeTypes.value.includes(mimeType));

    if (!allCorrectMimeTypes) {
      throw new Error('One or more files contains an unacceptable mime type.');
    }

    const allCorrectFileExtensions = files.every((file) => {
      const extension = file.name.split('.').pop();

      return extension && acceptedFileExtensions.value.includes(extension);
    });

    if (!allCorrectFileExtensions) {
      throw new Error('One or more files contains an unacceptable extension.');
    }

    return true;
  }

  async function processFiles(files: File[]) {
    try {
      await areFileTypesAccepted(files);

      emit('file-select', { files });
    } catch (error) {
      handleFileError(error as Error);
    }
  }

  /**
   * Sets file(s) to selected file(s) from dialogue
   * @param {Object} event - file select event that contains file(s)
   * @returns {Array} An array of files
   */
  function handleFileInput(event: Event) {
    const files = [...((event.target as HTMLInputElement)?.files || [])];

    processFiles(files);
  }

  /**
   * Sets file to dropped file if it is proper file type
   * @param {Object} event - file select event that contains file
   */
  function handleDropFile(event: DragEvent) {
    if (props.disabled) {
      return;
    }

    const files = [...(event.dataTransfer?.files || [])];

    isDraggingOver.value = false;

    return processFiles(files);
  }

  function handleFileDelete(file: File) {
    emit('file-delete', file);
  }

  function readMimeType(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      if (file.type) {
        return resolve(file.type);
      } else if (window.FileReader) {
        const fileReader = new FileReader();

        fileReader.onload = () => {
          const mimeType =
            fileReader.result && (fileReader.result as string).match(/[^:]\w+\/[\w-+\d.]+(?=;|,)/)
              ? ((fileReader.result as string).match(/[^:]\w+\/[\w-+\d.]+(?=;|,)/) as string[])[0]
              : '';

          resolve(mimeType);
        };

        fileReader.readAsDataURL(file);
      } else {
        reject(new Error('Failed to read file.'));
      }
    });
  }
</script>

<template>
  <div class="stash-file-upload" :class="attributes.class" data-test="stash-file-upload">
    <div v-if="buttonOnly">
      <Button secondary type="button" :disabled="props.disabled" @click.stop.prevent="openFileDialog">
        <slot name="submitText">
          {{ t('ll.fileUpload.uploadFile') }}
        </slot>
      </Button>
    </div>
    <div
      v-else
      class="rounded p-6"
      :class="[
        classes['file-dropbox'],
        {
          [classes['is-dragging']]: isDraggingOver,
          [classes['is-disabled']]: props.disabled,
        },
      ]"
      @dragover.prevent="handleDragEnter"
      @drop.prevent="handleDropFile"
      @dragleave.prevent="handleDragLeave"
    >
      <div
        class="flex flex-col items-center justify-center text-center"
        :class="[{ 'items-center md:flex-row': size === FileUploadSizes.Dense }]"
      >
        <template v-if="!files.length">
          <InlineSvg v-if="size !== FileUploadSizes.Dense" :src="illustrationPath" name="file" width="84" height="96" />
          <span class="text-ice-900">
            {{ t('ll.fileUpload.dragDropFileHere') }}
          </span>
          <span
            :class="size === FileUploadSizes.Dense ? 'md:ml-1.5 md:mr-3 md:my-0 my-1.5 text-ice-900' : 'mt-1.5 my-1.5'"
          >
            {{ t('ll.fileUpload.or') }}
          </span>
          <Button
            class="mt-1.5"
            secondary
            type="button"
            :class="classes['file-select-button']"
            :disabled="disabled"
            @click.stop.prevent="openFileDialog"
          >
            <!-- @slot for custom submit text -->
            <slot name="submitText">{{ t('ll.fileUpload.uploadFile') }}</slot>
          </Button>
        </template>
        <template v-else>
          <div v-for="file in files" :key="file.name">
            <Icon name="file" />
            <span>{{ file.name }}</span>
            <Button :class="[classes['remove-button'], classes['button']]" @click.stop.prevent="handleFileDelete(file)">
              {{ t('ll.fileUpload.remove') }}
            </Button>
          </div>
        </template>
      </div>
      <div v-if="$slots.hint && !files.length" class="mt-6 text-center text-xs text-ice-700">
        <!-- @slot for displaying helpful text and/or links -->
        <slot name="hint"></slot>
      </div>
    </div>
    <input
      v-show="false"
      v-bind="inputAttrs"
      ref="fileUploadRef"
      data-test="stash-file-upload|input"
      type="file"
      :disabled="disabled"
      :accept="acceptedMimeTypes.join(',')"
      :multiple="props.multiple"
      @change="handleFileInput"
    />
  </div>
</template>

<style module>
  @layer utilities {
    .file-dropbox {
      background: var(--color-ice-200);
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='4' ry='4' stroke='%23C5C9D4FF' stroke-width='1' stroke-dasharray='5 %2c 5' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
      background-repeat: no-repeat;
      border: 1px solid var(--color-ice-500);
      border-color: transparent;
    }

    .is-dragging {
      background-image: none;
      border-color: var(--color-ice-500);

      & > * {
        pointer-events: none;
      }
    }

    .is-disabled {
      cursor: no-drop;
    }

    /* Constrain the upload icon for drag/drop to the required size */
    .upload-icon {
      height: 98px;
      width: 84px;
    }

    .remove-button.button {
      background: transparent;
      border: none;
      color: var(--color-red-500);

      &:hover {
        background: transparent;
        border: none;
      }
    }
  }
</style>
